import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { sendNowhere } from "../../view.ts";
import { type Action } from "../../app-store.ts";
import * as Styles from "../../../styles.ts";

// Components
import "../artifact-card.ts";
import "./canvas-panel.ts";
import "../../dui/vstack/dui-vstack.ts";
import "../../dui/hstack/dui-hstack.ts";
import "../../dui/page-inset/dui-page-inset.ts";

const styles = css`
  :host {
    display: block;
    height: 100cqh;
    width: 100cqw;
    container-type: size;
  }
`;

const ArtifactView = ({
  title,
  content,
  disabled,
  onClick,
}: {
  title: string;
  content: string;
  disabled: boolean;
  onClick: (event: MouseEvent) => void;
}): TemplateResult => {
  return html`
    <artifact-card ?disabled="${disabled}" @click="${onClick}">
      <div slot="title">
        <div>${title}</div>
      </div>
      <div>${content}</div>
    </artifact-card>
  `;
};

@customElement("index-panel")
export class IndexPanel extends LitElement {
  static override styles = [Styles.base, styles];

  @property({ type: String })
  accessor heading = "Scenario Phases";

  @property({ attribute: false })
  accessor send = sendNowhere<Action>;

  override render() {
    const onClickFocus = () => {
      this.send({
        type: "set-canvas-view",
        canvasView: "project-info",
        slug: undefined,
      });
    };

    const onClickForceTable = () => {
      this.send({
        type: "set-canvas-view",
        canvasView: "force-table",
        slug: undefined,
      });
    };

    const onClickForceVis = () => {
      this.send({
        type: "set-canvas-view",
        canvasView: "force-vis",
        slug: undefined,
      });
    };

    const onClickForceMap = () => {
      this.send({
        type: "set-canvas-view",
        canvasView: "force-map",
        slug: undefined,
      });
    };

    const onClickReport = () => {
      this.send({
        type: "set-canvas-view",
        canvasView: "report",
        slug: undefined,
      });
    };

    return html`
      <canvas-panel no-close inset>
        <div slot="title">${this.heading}</div>
        <dui-page-inset>
          <dui-vstack>
            ${ArtifactView({
              title: "1. Focus",
              content: "Scope and purpose of our scenario project",
              disabled: false,
              onClick: onClickFocus,
            })}
            ${ArtifactView({
              title: "2. Scan Environment",
              content: "What's changing? How fast? What's driving it?",
              disabled: false,
              onClick: onClickForceTable,
            })}
            ${ArtifactView({
              title: "3. Connect forces",
              content:
                "Uncover structural relationships, confluences, and patterns",
              disabled: false,
              onClick: onClickForceVis,
            })}
            ${ArtifactView({
              title: "4. Map forces",
              content:
                "Chart uncertainty and impact. Find risk areas, opportunities, and plot twists",
              disabled: false,
              onClick: onClickForceMap,
            })}
            ${ArtifactView({
              title: "5. Strategic report",
              content:
                "Implications for focus area, leverage points, signposts to watch, opportunities",
              disabled: false,
              onClick: onClickReport,
            })}
          </dui-vstack>
        </dui-page-inset>
      </canvas-panel>
    `;
  }
}
